/**
 * Drag the show sheet down to dismiss it, from anywhere on it.
 *
 * Why this is not just pointer events: on iOS, Safari decides what a vertical
 * swipe means before the page gets a say. Over a scrolling element it claims
 * the gesture and the pointer stream stops. The one place the old version
 * worked was the grip, and the only thing special about the grip is
 * `touch-action: none` — which cannot go on the episode list without killing
 * scrolling there.
 *
 * So touch is handled natively with a non-passive `touchmove` listener, and
 * the drag calls `preventDefault()` once it has started. That is the only
 * thing that takes the gesture back from Safari, and it is not available to a
 * passive React handler. Mouse still goes through the pointer handlers, which
 * is what the desktop build uses.
 *
 * Two conditions keep the gesture out of the way of everything else:
 *
 *   - the scrolling body must be at its top, so a downward swipe lower down
 *     scrolls the episode list as it always did. The grip is exempt: it does
 *     not scroll, so it always drags.
 *   - the finger must travel past a few pixels of slop before anything moves,
 *     so a tap on an episode is a tap. An upward swipe disarms the gesture
 *     for good, handing the rest of it back to the scroller.
 */
import { useEffect, useRef, useState } from "react";
import type { RefObject } from "react";

/** Past this many pixels the sheet dismisses instead of springing back. */
const DISMISS_PX = 110;

/** Below this the gesture is still a tap, and buttons keep working. */
const SLOP_PX = 8;

interface Drag {
  y: number;
  armed: boolean;
}

/** A drag may start here: on the grip, or with the list already at its top. */
function armedAt(target: EventTarget | null, body: RefObject<HTMLDivElement | null>): boolean {
  if (target instanceof Element && target.closest(".sheet-grip") !== null) return true;
  return (body.current?.scrollTop ?? 0) <= 0;
}

export function useDragToClose(onClose: () => void, body: RefObject<HTMLDivElement | null>) {
  const [offset, setOffset] = useState<number | null>(null);
  const sheet = useRef<HTMLDivElement>(null);
  const drag = useRef<Drag | null>(null);
  // Mirrors of state, for the native listeners, which are bound once and
  // would otherwise close over the first render's values.
  const moved = useRef(0);
  const dragging = useRef(false);
  const close = useRef(onClose);
  close.current = onClose;

  const settle = () => {
    drag.current = null;
    dragging.current = false;
    if (moved.current > DISMISS_PX) close.current();
    moved.current = 0;
    setOffset(null);
  };

  useEffect(() => {
    const el = sheet.current;
    if (el === null) return;

    const onStart = (event: TouchEvent) => {
      const touch = event.touches[0];
      if (touch === undefined) return;
      drag.current = { y: touch.clientY, armed: armedAt(event.target, body) };
      dragging.current = false;
      moved.current = 0;
    };

    const onMove = (event: TouchEvent) => {
      const from = drag.current;
      const touch = event.touches[0];
      if (from === null || !from.armed || touch === undefined) return;
      const delta = touch.clientY - from.y;
      if (!dragging.current) {
        // An upward swipe is a scroll, and stays one for the rest of the
        // gesture even if the finger comes back down past the start.
        if (delta < -SLOP_PX) drag.current = { ...from, armed: false };
        if (delta < SLOP_PX) return;
        dragging.current = true;
      }
      // Takes the gesture back from Safari. Needs a non-passive listener.
      event.preventDefault();
      moved.current = delta;
      setOffset(delta);
    };

    el.addEventListener("touchstart", onStart, { passive: true });
    el.addEventListener("touchmove", onMove, { passive: false });
    el.addEventListener("touchend", settle);
    el.addEventListener("touchcancel", settle);
    return () => {
      el.removeEventListener("touchstart", onStart);
      el.removeEventListener("touchmove", onMove);
      el.removeEventListener("touchend", settle);
      el.removeEventListener("touchcancel", settle);
    };
    // settle is stable in everything that matters: it reads refs only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [body]);

  /** Mouse only. Touch is handled above; a pointer stream for it is a duplicate. */
  const handlers = {
    onPointerDown: (event: React.PointerEvent) => {
      if (event.pointerType === "touch") return;
      drag.current = { y: event.clientY, armed: armedAt(event.target, body) };
      dragging.current = false;
      moved.current = 0;
    },
    onPointerMove: (event: React.PointerEvent) => {
      const from = drag.current;
      if (event.pointerType === "touch" || from === null || !from.armed) return;
      const delta = event.clientY - from.y;
      if (!dragging.current) {
        if (delta < SLOP_PX) return;
        dragging.current = true;
        // Only once the drag is real: capturing earlier would swallow the
        // click on whatever is under the cursor.
        event.currentTarget.setPointerCapture(event.pointerId);
      }
      moved.current = delta;
      setOffset(delta);
    },
    onPointerUp: (event: React.PointerEvent) => {
      if (event.pointerType === "touch") return;
      settle();
    },
  };

  return { offset, sheet, handlers };
}
